import { useState } from "react";
import { useForm } from "react-hook-form";

import { addSource, editSource } from "services";
import { useAppDispatch } from "store/store";
import { useSnackbar } from "providers/SnackbarProvider";
import { useConfirmDialogExit } from "hooks";
import {
  SingleStepDialog,
  SingleStepHeaders,
  SingleStepBody,
  SingleStepFooter,
} from "components/SingleStepDialog";
import {
  setStatusUploadSource,
  StatusUploadSource,
  setAddSourceNameValidation,
} from "store/slicer/FormAddSourceValidationSlice";
import { Source, SourceRequest } from "interfaces";
import {
  formAddSource,
  snackbarDataUploaded,
  snackbarSummarySourceAddSuccessSubTitlePart1,
  snackbarSummarySourceAddSuccessSubTitlePart2,
  snackbarEditSourceSuccessSubTitlePart2,
  snackbarEditSource,
  modalEditSourceSubTitle,
  modalEditSourceTitle,
  modalSourcePrivateTitle,
  modalSourceSubTitle,
  buttonSaveUpdate,
  buttonAdd,
  modalEditInfo,
} from "_constants";
import { setNameTopicValidation } from "store/slicer/FormTopicValidationSlice";
import { resetLoadedStatus } from "store/slicer/GestioneDetailProjectSlice";
import { FormSource } from "components/form/FormSource";
import {
  setPageSources,
  updateSource,
} from "store/slicer/GestioneSourcesSlice";
import { setSource } from "store/slicer/GestioneSourceSlice";

export const ModalPrivateSource = ({
  modalVisibile = false,
  projectId,
  source,
  onClose,
  isPrivate = true,
}: {
  modalVisibile: boolean;
  projectId?: string;
  source?: Source;
  onClose: Function;
  isPrivate?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const [visible, setVisible] = useState(modalVisibile);
  const { showSnackbar } = useSnackbar();
  const { showConfirmDialogExit } = useConfirmDialogExit();

  const { handleSubmit, control, getValues, trigger, setValue, getFieldState } =
    useForm({
      defaultValues: {
        fileId: source?.fileId ?? "",
        name: source?.name ?? "",
        author: source?.author ?? "",
        isbn: source?.isbn ?? "",
        topicId: source?.topicId ?? "",
      },
      shouldFocusError: false,
      mode: "onChange",
    });

  const onSubmit = (formData: any) => {
    if (source) {
      const request: SourceRequest = {
        id: source.id,
        fileId: formData.fileId,
        name: formData.name,
        author: formData.author,
        isbn: formData.isbn ? formData.isbn : null,
        topicId: formData.topic?.id,
        projectId: projectId,
      };
      editSource(request)
        .then((response) => {
          showSnackbar({
            severity: "success",
            summary: snackbarEditSource,
            detail: `${snackbarSummarySourceAddSuccessSubTitlePart1}${getValues(
              "name"
            )}${snackbarEditSourceSuccessSubTitlePart2}`,
            life: 5000,
          });

          dispatch(setSource(response.data));
          dispatch(updateSource(response.data));
          dispatch(setPageSources(-1));
          dispatch(resetLoadedStatus());

          setTimeout(() => {
            dispatch(setPageSources(1));
          }, 200);
          resetFormState(false);
          onClose();
        })
        .catch((error) => {
          if (error?.response?.status === 409) {
            dispatch(setNameTopicValidation(false));
          }
        });
    } else {
      const request: SourceRequest = {
        fileId: formData.fileId,
        name: formData.name,
        author: formData.author,
        isbn: formData.isbn ? formData.isbn : null,
        topicId: formData.topic?.id,
        projectId: projectId,
      };
      addSource(request)
        .then((_) => {
          showSnackbar({
            severity: "success",
            summary: snackbarDataUploaded,
            detail: `${snackbarSummarySourceAddSuccessSubTitlePart1}${getValues(
              "name"
            )}${snackbarSummarySourceAddSuccessSubTitlePart2}`,
            life: 5000,
          });

          dispatch(resetLoadedStatus());
          resetFormState(false);
        })
        .catch((error) => {
          if (error?.response?.status === 409) {
            dispatch(setNameTopicValidation(false));
          }
        });
    }
  };

  const handleDismiss = () => {
    if (control._getDirty()) {
      const values = getValues();
      const isFormFilled = Object.values(values).some(
        (value) => value !== "" && value !== undefined
      );
      if (isFormFilled) {
        showConfirmDialogExit({ rejectCallback: resetFormState });
      } else {
        resetFormState();
      }
    } else {
      resetFormState();
    }
  };

  const resetFormState = (interruptUpload: boolean = true) => {
    if (interruptUpload) {
      dispatch(setStatusUploadSource(StatusUploadSource.CANCEL));
    }

    dispatch(setAddSourceNameValidation(true));
    control._reset();
    setVisible(false);
    onClose();
  };

  return (
    <SingleStepDialog
      onHideCallback={handleDismiss}
      minHeight="507px"
      width="700px"
      maxWidth="700px"
      size="auto"
      header={
        <SingleStepHeaders
          title={
            isPrivate
              ? source
                ? modalEditSourceTitle
                : modalSourcePrivateTitle
              : source
              ? modalEditInfo
              : modalSourcePrivateTitle
          }
          subtitle={source ? modalEditSourceSubTitle : modalSourceSubTitle}
        />
      }
      body={
        <SingleStepBody
          content={
            <form
              id={formAddSource}
              onSubmit={handleSubmit(onSubmit)}
              className="width-100"
            >
              <FormSource
                control={control}
                getValues={getValues}
                trigger={trigger}
                setValue={setValue}
                getFieldState={getFieldState}
                projectId={projectId}
                isPrivate={isPrivate}
              />
            </form>
          }
        />
      }
      footer={
        <SingleStepFooter
          dismiss={handleDismiss}
          form={formAddSource}
          labelSubmit={source?.id ? buttonSaveUpdate : buttonAdd}
        />
      }
      display={visible}
      setVisible={setVisible}
    />
  );
};
